import { format, subMonths } from 'date-fns';
import { useMemo } from 'react';
import { useQuery } from 'urql';

import {
  COMPOSITION_GET_DATA_V2,
  Dimension1,
  ENTITY_V2_QUERY,
  EntityV2Type,
  POSTING_GET_DATA,
  POSTING_SOURCE_IDS,
  PositionsEntityKind,
  SENTIMENT_GET_MAP_DATA,
} from '@revelio/data-access';

import { parseEntity } from '../hooks/useCompetitorComposition';
import {
  buildCustomRoleFilter,
  type RoleLevel,
} from '../hooks/useResolveProfileIds';
import { parseSentimentEntry } from '../hooks/useSentimentComparison';

export interface CompanyPeekExtra {
  description: string | null;
  headquarters: string | null;
  founded: string | null;
  activePostingsForRole: number | null;
  /** Lazy-fetched headcount (fallback when parent doesn't provide it) */
  headcount: number | null;
  /** Lazy-fetched salary (fallback when parent doesn't provide it) */
  salary: number | null;
  /** Lazy-fetched growth rate */
  growthRate: number | null;
  /** Lazy-fetched sentiment overall */
  sentimentOverall: number | null;
  isLoading: boolean;
}

const getDateRange = () => {
  const now = new Date();
  return {
    start_date: format(subMonths(now, 6), 'yyyy-MM-dd'),
    end_date: format(now, 'yyyy-MM-dd'),
  };
};

export const useCompanyPeekData = (
  rcid: number | null,
  userRoleLevel: RoleLevel | null,
  isOpen: boolean
): CompanyPeekExtra => {
  const shouldFetch = isOpen && rcid !== null && rcid > 0;

  // Entity V2 — description, HQ, founded
  const [entityResult] = useQuery({
    query: ENTITY_V2_QUERY,
    variables: { req: { entID: rcid ?? 0, entType: EntityV2Type.Company } },
    pause: !shouldFetch,
  });

  const dateRange = useMemo(() => getDateRange(), []);

  // Postings — active count for user's role
  const postingFilters = useMemo(() => {
    const filters: Record<string, unknown> = {
      company: rcid ? [rcid] : [],
      provider: [POSTING_SOURCE_IDS.unified],
      metric_mode: 'posting',
      start_date: dateRange.start_date,
      end_date: dateRange.end_date,
    };
    if (userRoleLevel) {
      filters.custom_role = buildCustomRoleFilter(userRoleLevel);
    }
    return filters;
  }, [rcid, userRoleLevel, dateRange]);

  const [postingsResult] = useQuery({
    query: POSTING_GET_DATA,
    variables: {
      filters: postingFilters,
      dim1: Dimension1.Company,
    },
    pause: !shouldFetch,
  });

  // Composition — headcount, salary, growthRate
  const compositionEntities = useMemo(
    () => (rcid ? [{ EntID: rcid, EntType: PositionsEntityKind.Company }] : []),
    [rcid]
  );

  const [compositionResult] = useQuery({
    query: COMPOSITION_GET_DATA_V2,
    variables: {
      entities: compositionEntities,
      filters: { raw: false },
    },
    pause: !shouldFetch,
  });

  // Sentiment — overall score
  const [sentimentResult] = useQuery({
    query: SENTIMENT_GET_MAP_DATA,
    variables: {
      filters: { company: rcid ? [rcid] : [] },
      dim1: Dimension1.Company,
    },
    pause: !shouldFetch,
  });

  const activePostingsForRole = useMemo(() => {
    const entries = postingsResult.data?.posting;
    if (!entries || !Array.isArray(entries) || entries.length === 0) {
      return null;
    }

    const entry = entries[0];
    const categories = entry?.category;
    if (!categories || categories.length === 0) return null;

    const latest = categories[categories.length - 1];
    return latest?.metrics?.active ?? null;
  }, [postingsResult.data]);

  // Parse composition metrics
  const compositionMetrics = useMemo(() => {
    const rawEntities = compositionResult.data?.composition_v2?.entities;
    if (!rawEntities || rawEntities.length === 0) {
      return { headcount: null, salary: null, growthRate: null };
    }
    const first = rawEntities[0];
    if (!first) return { headcount: null, salary: null, growthRate: null };
    const parsed = parseEntity(first);
    return {
      headcount: parsed.headcount,
      salary: parsed.salary,
      growthRate: parsed.growthRate,
    };
  }, [compositionResult.data]);

  // Parse sentiment overall using the same parser as useSentimentComparison
  const sentimentOverall = useMemo(() => {
    const entries = sentimentResult.data?.sentiment;
    if (!entries || !Array.isArray(entries) || entries.length === 0) {
      return null;
    }
    const entry = entries[0];
    if (!entry) return null;
    const parsed = parseSentimentEntry(entry);
    return parsed.scores.overall ?? null;
  }, [sentimentResult.data]);

  const entity = entityResult.data?.entityV2?.entity;

  return {
    description: entity?.description ?? null,
    headquarters: entity?.headquarters ?? null,
    founded: entity?.founded ?? null,
    activePostingsForRole,
    headcount: compositionMetrics.headcount,
    salary: compositionMetrics.salary,
    growthRate: compositionMetrics.growthRate,
    sentimentOverall,
    isLoading:
      entityResult.fetching ||
      postingsResult.fetching ||
      compositionResult.fetching ||
      sentimentResult.fetching,
  };
};
